import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  IconButton,
  Tooltip,
  Button,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Collapse,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { bountyService } from '../../services/bountyService';
import { mitarbeiterService } from '../../services/mitarbeiterService';
import { useNotification } from '../../hooks/NotificationContext';
import { MitarbeiterList, BountyAnteilInput } from '../../types';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SEGMENT_COLORS = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0',
  '#00BCD4', '#F44336', '#8BC34A', '#FF5722',
];

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface LocalAnteil {
  mitarbeiter_id: number;
  anteil_prozent: number;
  vorname: string;
  nachname: string;
}

interface Preset {
  label: string;
  values: number[];
}

interface BountyVerteilungProps {
  schwachstelleId: number | null;
  editable: boolean;
  reporter?: MitarbeiterList;
}

/* ------------------------------------------------------------------ */
/*  Pure helpers                                                       */
/* ------------------------------------------------------------------ */

/** Convert anteil percentages to cumulative thumb positions. */
function anteileToThumbs(anteile: LocalAnteil[]): number[] {
  const thumbs: number[] = [];
  let cum = 0;
  for (let i = 0; i < anteile.length - 1; i++) {
    cum += anteile[i].anteil_prozent;
    thumbs.push(Math.round(cum));
  }
  return thumbs;
}

/** Convert thumb positions back to anteil percentages. */
function thumbsToAnteile(
  thumbs: number[],
  current: LocalAnteil[],
): LocalAnteil[] {
  return current.map((a, i) => {
    let pct: number;
    if (i === 0) pct = thumbs[0] ?? 100;
    else if (i === current.length - 1)
      pct = 100 - (thumbs[thumbs.length - 1] ?? 0);
    else pct = (thumbs[i] ?? 0) - (thumbs[i - 1] ?? 0);
    return { ...a, anteil_prozent: Math.max(0, pct) };
  });
}

/** Return preset distributions for a given pentester count. */
function getPresets(count: number): Preset[] {
  switch (count) {
    case 2:
      return [
        { label: '50 / 50', values: [50, 50] },
        { label: '60 / 40', values: [60, 40] },
        { label: '70 / 30', values: [70, 30] },
        { label: '80 / 20', values: [80, 20] },
      ];
    case 3:
      return [
        { label: '33 / 33 / 34', values: [33, 33, 34] },
        { label: '50 / 25 / 25', values: [50, 25, 25] },
        { label: '40 / 30 / 30', values: [40, 30, 30] },
      ];
    case 4:
      return [
        { label: '25 / 25 / 25 / 25', values: [25, 25, 25, 25] },
        { label: '40 / 20 / 20 / 20', values: [40, 20, 20, 20] },
      ];
    default: {
      if (count < 2) return [];
      const eq = Math.floor(100 / count);
      const last = 100 - eq * (count - 1);
      return [
        {
          label: 'Gleichverteilen',
          values: [...Array(count - 1).fill(eq), last],
        },
      ];
    }
  }
}

/**
 * Redistribute shares when one value is manually changed.
 * Keeps the total at exactly 100 by proportionally adjusting the others.
 */
function redistributeManual(
  anteile: LocalAnteil[],
  changedIndex: number,
  newValue: number,
): LocalAnteil[] {
  const n = anteile.length;
  if (n <= 1) return anteile;

  const clamped = Math.max(0, Math.min(100, Math.round(newValue)));
  const updated = anteile.map((a) => ({ ...a }));
  updated[changedIndex].anteil_prozent = clamped;

  const remaining = 100 - clamped;
  const otherIndices = anteile
    .map((_, i) => i)
    .filter((i) => i !== changedIndex);
  const othersSum = otherIndices.reduce(
    (s, i) => s + anteile[i].anteil_prozent,
    0,
  );

  if (othersSum > 0) {
    let distributed = 0;
    for (let j = 0; j < otherIndices.length; j++) {
      const idx = otherIndices[j];
      if (j === otherIndices.length - 1) {
        updated[idx].anteil_prozent = Math.max(0, remaining - distributed);
      } else {
        const share = Math.round(
          (anteile[idx].anteil_prozent / othersSum) * remaining,
        );
        updated[idx].anteil_prozent = Math.max(0, share);
        distributed += updated[idx].anteil_prozent;
      }
    }
  } else {
    const perOther = Math.floor(remaining / otherIndices.length);
    const rem = remaining - perOther * otherIndices.length;
    otherIndices.forEach((idx, j) => {
      updated[idx].anteil_prozent = perOther + (j < rem ? 1 : 0);
    });
  }

  return updated;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const BountyVerteilung: React.FC<BountyVerteilungProps> = ({
  schwachstelleId,
  editable,
  reporter,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError } = useNotification();

  const [anteile, setAnteile] = useState<LocalAnteil[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<MitarbeiterList[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddValue, setQuickAddValue] = useState<MitarbeiterList | null>(null);
  const [lastPresetLabel, setLastPresetLabel] = useState<string | null>(null);
  const [lastPresetReversed, setLastPresetReversed] = useState(false);

  const summe = anteile.reduce((s, a) => s + a.anteil_prozent, 0);
  const summeRounded = Math.round(summe * 100) / 100;

  /* ---------- data loading ---------- */

  const loadData = useCallback(async () => {
    if (!schwachstelleId) return;
    setLoading(true);
    try {
      const [verteilung, mitarbeiterData] = await Promise.all([
        bountyService.get(schwachstelleId),
        mitarbeiterService.list(),
      ]);
      const loaded = verteilung.anteile.map((a) => ({
        mitarbeiter_id: a.mitarbeiter_id,
        anteil_prozent: a.anteil_prozent,
        vorname: a.mitarbeiter.vorname,
        nachname: a.mitarbeiter.nachname,
      }));

      if (loaded.length === 0 && reporter) {
        setAnteile([{
          mitarbeiter_id: reporter.id,
          anteil_prozent: 100,
          vorname: reporter.vorname,
          nachname: reporter.nachname,
        }]);
        setExpanded(true);
      } else {
        setAnteile(loaded);
        if (loaded.length > 0) setExpanded(true);
      }
      setMitarbeiter(mitarbeiterData);
    } catch {
      // optional component – silent fail
    } finally {
      setLoading(false);
    }
  }, [schwachstelleId, reporter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ---------- handlers ---------- */

  const addPentesters = (newEntries: MitarbeiterList[]) => {
    const uniqueNew = newEntries.filter(
      (m) => !anteile.some((a) => a.mitarbeiter_id === m.id),
    );
    if (uniqueNew.length === 0) return;

    const all: LocalAnteil[] = [
      ...anteile,
      ...uniqueNew.map((m) => ({
        mitarbeiter_id: m.id,
        anteil_prozent: 0,
        vorname: m.vorname,
        nachname: m.nachname,
      })),
    ];
    const n = all.length;
    const eq = Math.floor(100 / n);
    const rem = 100 - eq * n;
    setAnteile(
      all.map((a, i) => ({ ...a, anteil_prozent: eq + (i < rem ? 1 : 0) })),
    );
    setDirty(true);
    if (!expanded) setExpanded(true);
  };

  const handleRemove = (mitarbeiterId: number) => {
    setAnteile((prev) => {
      const removed = prev.find((a) => a.mitarbeiter_id === mitarbeiterId);
      const rest = prev.filter((a) => a.mitarbeiter_id !== mitarbeiterId);
      if (rest.length === 0) return [];
      if (rest.length === 1) return [{ ...rest[0], anteil_prozent: 100 }];

      const freed = removed?.anteil_prozent ?? 0;
      const curSum = rest.reduce((s, a) => s + a.anteil_prozent, 0);

      if (curSum > 0) {
        let distributed = 0;
        return rest.map((a, i) => {
          if (i === rest.length - 1)
            return {
              ...a,
              anteil_prozent: a.anteil_prozent + freed - distributed,
            };
          const extra = Math.round((a.anteil_prozent / curSum) * freed);
          distributed += extra;
          return { ...a, anteil_prozent: a.anteil_prozent + extra };
        });
      }

      const eq = Math.round(100 / rest.length);
      return rest.map((a, i) => ({
        ...a,
        anteil_prozent:
          i === rest.length - 1 ? 100 - eq * (rest.length - 1) : eq,
      }));
    });
    setDirty(true);
  };

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const t = Array.isArray(value) ? value : [value];
    setAnteile((prev) => thumbsToAnteile(t, prev));
    setDirty(true);
  };

  const handleManualInput = (index: number, raw: string) => {
    if (raw === '') {
      setAnteile((prev) => redistributeManual(prev, index, 0));
      setDirty(true);
      return;
    }
    const parsed = Number(raw);
    if (isNaN(parsed)) return;
    setAnteile((prev) => redistributeManual(prev, index, parsed));
    setDirty(true);
  };

  const applyPreset = (label: string, values: number[]) => {
    const shouldReverse = label === lastPresetLabel ? !lastPresetReversed : false;
    const nextValues = shouldReverse ? [...values].reverse() : values;
    setAnteile((prev) =>
      prev.map((a, i) => ({ ...a, anteil_prozent: nextValues[i] ?? 0 })),
    );
    setLastPresetLabel(label);
    setLastPresetReversed(shouldReverse);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!schwachstelleId) return;
    setSaving(true);
    try {
      const payload: BountyAnteilInput[] = anteile.map((a) => ({
        mitarbeiter_id: a.mitarbeiter_id,
        anteil_prozent: a.anteil_prozent,
      }));
      const result = await bountyService.update(schwachstelleId, payload);
      setAnteile(
        result.anteile.map((a) => ({
          mitarbeiter_id: a.mitarbeiter_id,
          anteil_prozent: a.anteil_prozent,
          vorname: a.mitarbeiter.vorname,
          nachname: a.mitarbeiter.nachname,
        })),
      );
      setDirty(false);
      showSuccess('Bounty-Verteilung gespeichert');
    } catch {
      showError('Fehler beim Speichern der Bounty-Verteilung');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- derived ---------- */

  const availableMitarbeiter = mitarbeiter.filter(
    (m) => m.aktiv && !anteile.some((a) => a.mitarbeiter_id === m.id),
  );
  const presets = getPresets(anteile.length);
  const thumbs = anteileToThumbs(anteile);
  const col = (i: number) => SEGMENT_COLORS[i % SEGMENT_COLORS.length];
  const segmentGradient = (() => {
    if (anteile.length === 0) return 'transparent';
    let cursor = 0;
    const stops: string[] = [];
    anteile.forEach((a, i) => {
      const start = cursor;
      const end = Math.min(100, start + Math.max(0, a.anteil_prozent));
      const c = alpha(col(i), isDark ? 0.65 : 0.55);
      stops.push(`${c} ${start}%`, `${c} ${end}%`);
      cursor = end;
    });
    if (cursor < 100) {
      const rest = alpha(theme.palette.text.disabled, isDark ? 0.25 : 0.18);
      stops.push(`${rest} ${cursor}%`, `${rest} 100%`);
    }
    return `linear-gradient(90deg, ${stops.join(', ')})`;
  })();
  const thumbColorSx = thumbs.reduce<Record<string, object>>((acc, _, idx) => {
    const thumbColor = col(idx);
    const thumbStyle = {
      backgroundColor: thumbColor,
      borderColor: alpha(thumbColor, 0.7),
      '& .MuiSlider-valueLabel': {
        backgroundColor: thumbColor,
      },
    };
    // data-index works in most MUI builds; nth-of-type is a fallback.
    acc[`& .MuiSlider-thumb[data-index="${idx}"]`] = thumbStyle;
    acc[`& .MuiSlider-thumb:nth-of-type(${idx + 1})`] = thumbStyle;
    return acc;
  }, {});

  const getSummeColor = () => {
    if (summeRounded === 100) return theme.palette.success.main;
    if (summeRounded > 100) return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  if (!schwachstelleId) return null;

  /* ---------- render ---------- */

  return (
    <Card
      sx={{
        mb: 3,
        border: `1px solid ${isDark ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.15)}`,
        bgcolor: isDark
          ? alpha(theme.palette.primary.main, 0.03)
          : alpha(theme.palette.primary.main, 0.02),
      }}
    >
      {/* ── Header ── */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: isDark
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        <EmojiEventsIcon sx={{ color: '#ffc107', fontSize: 22 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Bug Bounty Verteilung
        </Typography>
        {anteile.length > 0 && (
          <Chip
            label={`${anteile.length} Pentester · ${summeRounded}%`}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              bgcolor: alpha(getSummeColor(), 0.1),
              color: getSummeColor(),
              border: `1px solid ${alpha(getSummeColor(), 0.3)}`,
            }}
          />
        )}
        {expanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <CardContent sx={{ pt: 2, pb: 2, '&:last-child': { pb: 2 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* ── Pentester labels + inputs ── */}
              {anteile.length > 0 && (
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}
                >
                  {anteile.map((a, i) => (
                    <Box
                      key={a.mitarbeiter_id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: alpha(col(i), isDark ? 0.15 : 0.08),
                        border: `1px solid ${alpha(col(i), isDark ? 0.3 : 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: col(i),
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          fontSize: '0.8rem',
                        }}
                      >
                        {a.vorname} {a.nachname}
                      </Typography>

                      {editable && anteile.length > 1 ? (
                        <>
                          <TextField
                            type="number"
                            value={a.anteil_prozent}
                            onChange={(e) =>
                              handleManualInput(i, e.target.value)
                            }
                            size="small"
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            sx={{
                              width: 56,
                              ml: 0.5,
                              '& .MuiInputBase-input': {
                                py: '3px',
                                px: '4px',
                                textAlign: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                fontFamily: 'monospace',
                              },
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: alpha(col(i), 0.3),
                                },
                              },
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                          >
                            %
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            ml: 0.5,
                          }}
                        >
                          {a.anteil_prozent}%
                        </Typography>
                      )}

                      {editable && (
                        <Tooltip title="Entfernen">
                          <IconButton
                            size="small"
                            onClick={() => handleRemove(a.mitarbeiter_id)}
                            sx={{
                              p: 0.25,
                              ml: 0.25,
                              color: theme.palette.error.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}

                  {editable && availableMitarbeiter.length > 0 && (
                    quickAddOpen ? (
                      <Autocomplete
                        value={quickAddValue}
                        onChange={(_, val) => {
                          if (val) {
                            addPentesters([val]);
                          }
                          setQuickAddValue(null);
                          setQuickAddOpen(false);
                        }}
                        options={availableMitarbeiter}
                        getOptionLabel={(o) => `${o.vorname} ${o.nachname}`}
                        open
                        onClose={() => {
                          setQuickAddValue(null);
                          setQuickAddOpen(false);
                        }}
                        size="small"
                        sx={{ minWidth: 220 }}
                        noOptionsText="Kein Mitarbeiter verfügbar"
                        slotProps={{
                          paper: {
                            sx: {
                              bgcolor: isDark ? '#262b50' : 'background.paper',
                              border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                              boxShadow: isDark
                                ? '0 8px 24px rgba(0,0,0,0.5)'
                                : '0 8px 24px rgba(0,0,0,0.15)',
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            autoFocus
                            placeholder="Pentester auswählen..."
                            variant="outlined"
                            size="small"
                          />
                        )}
                      />
                    ) : (
                      <Tooltip title="Pentester hinzufügen">
                        <IconButton
                          size="small"
                          onClick={() => setQuickAddOpen(true)}
                          sx={{
                            border: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            height: 32,
                            width: 32,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.15),
                            },
                          }}
                        >
                          <AddIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </Box>
              )}

              {/* ── Multi-thumb slider (edit mode, 2+ pentesters) ── */}
              {editable && anteile.length >= 2 && (
                <Box sx={{ position: 'relative', mb: 1.5 }}>
                  {/* Colored segments behind the slider */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      right: 0,
                      transform: 'translateY(-50%)',
                      height: 14,
                      borderRadius: 7,
                      display: 'flex',
                      overflow: 'hidden',
                      pointerEvents: 'none',
                      zIndex: 0,
                      background: segmentGradient,
                    }}
                  />

                  <Slider
                    value={thumbs}
                    onChange={handleSliderChange}
                    min={0}
                    max={100}
                    step={1}
                    track={false}
                    disableSwap
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    sx={{
                      height: 14,
                      zIndex: 1,
                      '& .MuiSlider-rail': { opacity: 0 },
                      '& .MuiSlider-track': { display: 'none' },
                      '& .MuiSlider-thumb': {
                        width: 6,
                        height: 28,
                        borderRadius: 3,
                        backgroundColor: isDark
                          ? 'rgba(255,255,255,0.9)'
                          : '#fff',
                        border: '2px solid',
                        borderColor: isDark
                          ? 'rgba(255,255,255,0.5)'
                          : 'rgba(0,0,0,0.4)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: isDark
                            ? '0 0 0 6px rgba(255,255,255,0.12)'
                            : '0 0 0 6px rgba(0,0,0,0.08)',
                        },
                        '&.Mui-active': {
                          boxShadow: isDark
                            ? '0 0 0 8px rgba(255,255,255,0.16)'
                            : '0 0 0 8px rgba(0,0,0,0.12)',
                        },
                      },
                      '& .MuiSlider-valueLabel': {
                        fontSize: '0.7rem',
                        p: '2px 6px',
                      },
                      ...thumbColorSx,
                    }}
                  />
                </Box>
              )}

              {/* ── Single pentester bar (edit mode, exactly 1) ── */}
              {editable && anteile.length === 1 && (
                <Box
                  sx={{
                    height: 14,
                    borderRadius: 7,
                    bgcolor: alpha(col(0), isDark ? 0.55 : 0.45),
                    mb: 1.5,
                  }}
                />
              )}

              {/* ── Read-only stacked bar ── */}
              {!editable && anteile.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    height: 24,
                    borderRadius: 12,
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  {anteile.map((a, i) => (
                    <Tooltip
                      key={a.mitarbeiter_id}
                      title={`${a.vorname} ${a.nachname}: ${a.anteil_prozent}%`}
                    >
                      <Box
                        sx={{
                          width: `${a.anteil_prozent}%`,
                          height: '100%',
                          bgcolor: col(i),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: a.anteil_prozent > 0 ? 2 : 0,
                          transition: 'width 0.2s ease-out',
                        }}
                      >
                        {a.anteil_prozent >= 15 && (
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              color: '#fff',
                              fontWeight: 700,
                              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {a.anteil_prozent}%
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              )}

              {/* ── Preset buttons ── */}
              {editable && anteile.length >= 2 && presets.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    mb: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, mr: 0.5 }}
                  >
                    Presets:
                  </Typography>
                  {presets.map((p) => (
                    <Button
                      key={p.label}
                      size="small"
                      variant="outlined"
                      onClick={() => applyPreset(p.label, p.values)}
                      sx={{
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        minWidth: 0,
                        px: 1,
                        py: 0.25,
                        lineHeight: 1.4,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </Box>
              )}

              {/* ── Add pentester quick-add (empty state) ── */}
              {editable && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'center',
                    mb: dirty ? 1 : 0,
                  }}
                >
                  {anteile.length === 0 && availableMitarbeiter.length > 0 && (
                    <>
                      {quickAddOpen ? (
                        <Autocomplete
                          value={quickAddValue}
                          onChange={(_, val) => {
                            if (val) {
                              addPentesters([val]);
                            }
                            setQuickAddValue(null);
                            setQuickAddOpen(false);
                          }}
                          options={availableMitarbeiter}
                          getOptionLabel={(o) => `${o.vorname} ${o.nachname}`}
                          open
                          onClose={() => {
                            setQuickAddValue(null);
                            setQuickAddOpen(false);
                          }}
                          size="small"
                          sx={{ flex: 1, minWidth: 280 }}
                          noOptionsText="Kein Mitarbeiter verfügbar"
                          slotProps={{
                            paper: {
                              sx: {
                                bgcolor: isDark ? '#262b50' : 'background.paper',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                                boxShadow: isDark
                                  ? '0 8px 24px rgba(0,0,0,0.5)'
                                  : '0 8px 24px rgba(0,0,0,0.15)',
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              autoFocus
                              placeholder="Pentester auswählen..."
                              variant="outlined"
                              size="small"
                            />
                          )}
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setQuickAddOpen(true)}
                          sx={{ textTransform: 'none' }}
                        >
                          Pentester hinzufügen
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* ── Save button ── */}
              {editable && dirty && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || summeRounded > 100}
                    startIcon={
                      saving ? <CircularProgress size={14} /> : <SaveIcon />
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Verteilung speichern
                  </Button>
                </Box>
              )}

              {/* ── Empty state ── */}
              {anteile.length === 0 && !editable && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    py: 1,
                  }}
                >
                  Keine Bounty-Verteilung konfiguriert
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default BountyVerteilung;
